import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Dialog, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import { messageTypes } from '../../constants/mediaConstants';
import { showMessage } from '../../actions/globalActions';
import { LocalStorageService } from '../../services/localStorageService';
import { TokenService } from '../../services/tokenService';
import { loadQbrickEditor, QBRICK_EDITOR_TAG } from './qbrickEditorLoader';

import './QbrickEditorDialog.scss';
import { useTranslation } from 'react-i18next';

type QbrickEditorDialogProps = {
	open: boolean;
	onClose: (_isRefreshMedia?: boolean) => void;
};

// The rest of the app switches to a dark UI by tagging the body, mirror that in the editor
const getEditorTheme = () => (document.body.getAttribute('dark-mode') === 'true' ? 'dark' : 'light');

const QbrickEditorDialog = ({ open, onClose }: QbrickEditorDialogProps) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { selectedMediaForTrimming } = useSelector((state: any) => state.publish);
	const { defaultAccountId, accessToken } = useSelector((state: any) => state.session);

	// The dialog renders through a portal that mounts on a layout effect, so the host node is still
	// missing on the render that opens it. Tracking it as state re-runs the effect once it is there.
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const [isEditorLoading, setIsEditorLoading] = useState<boolean>(true);

	const mediaId = selectedMediaForTrimming?.id;
	const apiToken = accessToken || LocalStorageService.getAccessToken();

	// Keeps the editor element out of the render cycle. Everything it needs is read on use, so a
	// token refresh or a re-render never tears down an editor the user is working in.
	const latestRef = useRef({ dispatch, onClose, t, apiToken, defaultAccountId });
	latestRef.current = { dispatch, onClose, t, apiToken, defaultAccountId };

	useEffect(() => {
		if (!open || !mediaId || !container) {
			return;
		}

		let isCancelled = false;
		// Submitting an edit does not close the dialog, so the user can keep editing or produce
		// several outputs in one session. We remember that the library went stale and refresh it
		// once, on the way out, rather than reloading it underneath an open editor.
		let hasMediaChanged = false;
		// The editor fires once at five minutes out and again on expiry, and a 401 can fire it at any
		// time, so guard against overlapping refreshes racing two identity iframes.
		let isRefreshingToken = false;
		setIsEditorLoading(true);

		const editor = document.createElement(QBRICK_EDITOR_TAG) as any;
		// Set as properties rather than attributes: an attribute would put the token in the DOM
		// where any script on the page can read it. Properties assigned before the element is
		// upgraded are picked up when the bundle defines it.
		// Expiry first, then token: assigning the token is what adopts the pair. tokenExpiresAt is
		// what drives the editor's own "sign-in expires in N min" warning.
		editor.tokenExpiresAt = TokenService.getStoredTokenExpiresAt();
		editor.apiToken = latestRef.current.apiToken;
		editor.accountId = latestRef.current.defaultAccountId;
		editor.mediaId = mediaId;
		editor.theme = getEditorTheme();
		// Maps to the close-button attribute: the editor draws its own close button in its header,
		// so this dialog contributes no chrome of its own
		editor.closeButton = true;
		// encoderUrl is left unset on purpose, it defaults to the origin the bundle was served from

		const handleComplete = () => {
			const { dispatch: onDispatch, t: translate } = latestRef.current;
			hasMediaChanged = true;
			onDispatch(showMessage(translate('MODAL_QUICK_EDIT_CHANGES_ARE_BEING_PROCESSED'), messageTypes.success));
		};

		const handleJobFinished = (event: any) => {
			const { dispatch: onDispatch, t: translate } = latestRef.current;
			const status = String(event?.detail?.status ?? '').toLowerCase();
			const isFailed = status.includes('fail') || status.includes('error');
			if (!isFailed) {
				hasMediaChanged = true;
			}
			onDispatch(
				showMessage(
					isFailed ? translate('COMMON_ERROR') : translate('MODAL_QUICK_EDIT_JOB_FINISHED'),
					isFailed ? messageTypes.error : messageTypes.success
				)
			);
		};

		// The editor's own close button is the only thing that closes this dialog, so the user has
		// to actually mean it. event.detail carries hasUnsavedChanges if we ever want to confirm.
		const handleClose = () => {
			latestRef.current.onClose(hasMediaChanged);
		};

		// The editor warns 5 minutes out, again at expiry, and on any 401. Hand it a fresh token and
		// the session carries on untouched: unsaved edits, running uploads and drafts all survive.
		const handleTokenExpiring = () => {
			if (isRefreshingToken) {
				return;
			}
			isRefreshingToken = true;
			TokenService.requestFreshToken()
				.then(({ token, expiresAt }: { token: string; expiresAt: number }) => {
					isRefreshingToken = false;
					if (isCancelled) {
						return;
					}
					// Expiry first, then token: assigning the token is what adopts the pair
					editor.tokenExpiresAt = expiresAt;
					editor.apiToken = token;
				})
				.catch((error: any) => {
					isRefreshingToken = false;
					// Nothing to hand over, so the editor keeps its warning up and its autosaved
					// draft covers the user if the session does die.
					console.error('Could not refresh the Qbrick editor token', error);
				});
		};

		const handleError = (event: any) => {
			const { dispatch: onDispatch, t: translate } = latestRef.current;
			const error = event?.detail?.error;
			onDispatch(
				showMessage(
					error?.message || (typeof error === 'string' ? error : '') || translate('COMMON_ERROR'),
					messageTypes.error
				)
			);
		};

		editor.addEventListener('qbrick-editor:token-expiring', handleTokenExpiring);
		editor.addEventListener('qbrick-editor:close', handleClose);
		editor.addEventListener('qbrick-editor:complete', handleComplete);
		editor.addEventListener('qbrick-editor:job-finished', handleJobFinished);
		editor.addEventListener('qbrick-editor:error', handleError);

		container.appendChild(editor);

		loadQbrickEditor().then(
			() => {
				if (!isCancelled) {
					setIsEditorLoading(false);
				}
			},
			() => {
				if (isCancelled) {
					return;
				}
				setIsEditorLoading(false);
				latestRef.current.dispatch(
					showMessage(latestRef.current.t('MODAL_QUICK_EDIT_LOAD_EDITOR_FAILED'), messageTypes.error)
				);
			}
		);

		return () => {
			isCancelled = true;
			editor.removeEventListener('qbrick-editor:token-expiring', handleTokenExpiring);
			editor.removeEventListener('qbrick-editor:close', handleClose);
			editor.removeEventListener('qbrick-editor:complete', handleComplete);
			editor.removeEventListener('qbrick-editor:job-finished', handleJobFinished);
			editor.removeEventListener('qbrick-editor:error', handleError);
			editor.remove();
		};
	}, [open, mediaId, container]);

	return (
		<Dialog open={open} maxWidth={false} className="qbrick-editor-dialog">
			<div className="qbrick-editor-host" ref={setContainer} />
			{isEditorLoading && (
				<div className="qbrick-editor-loading">
					<CircularProgress />
					<Typography variant="body2">{t('COMMON_LOADING')}</Typography>
				</div>
			)}
		</Dialog>
	);
};

export default QbrickEditorDialog;
